import PropTypes from 'prop-types';
import { forwardRef, memo } from 'react';

import { noop } from 'lib/utils';

import Item from './Item';

/*
  Antes este wrapper controlava um `TextEditor` inline que aparecia
  abaixo de cada comentário ao clicar em "Responder". Agora a resposta
  é direcionada pro textbox único no rodapé da aba (via
  `activeAnswer` no `Comments.Root`), então o editor inline foi
  removido — o `CommentItem` voltou a ser um wrapper fino sobre o
  `Item`, mantido só por compatibilidade com os callers que importam
  este caminho.
*/
const CommentItem = forwardRef(
  (
    {
      canBeRecommendedAsReview,
      children,
      commentDate,
      commentFixed = false,
      commentId,
      commentLikes,
      commentStatusName,
      commentText,
      hasReply,
      isDisliked = false,
      isEdited,
      isLiked = false,
      isReply = false,
      isReview = false,
      isShowingReplies = false,
      parentId = null,
      userId,
      userLevel,
      userName,
      userPhoto,
      userSpecialtyName = null,
      userTypeName,
      userUsername,
      onToggleReplies = noop,
    },
    ref
  ) => (
    <Item
      ref={ref}
      canBeRecommendedAsReview={canBeRecommendedAsReview}
      commentDate={commentDate}
      commentFixed={commentFixed}
      commentId={commentId}
      commentLikes={commentLikes}
      commentStatusName={commentStatusName}
      commentText={commentText}
      hasReply={hasReply}
      isDisliked={isDisliked}
      isEdited={isEdited}
      isLiked={isLiked}
      isReply={isReply}
      isReview={isReview}
      isShowingReplies={isShowingReplies}
      parentId={parentId}
      userId={userId}
      userLevel={userLevel}
      userName={userName}
      userPhoto={userPhoto}
      userSpecialtyName={userSpecialtyName}
      userTypeName={userTypeName}
      userUsername={userUsername}
      onToggleReplies={onToggleReplies}
    >
      {children}
    </Item>
  )
);

CommentItem.displayName = 'CommentItem';

CommentItem.propTypes = {
  canBeRecommendedAsReview: PropTypes.bool,
  children: PropTypes.node,
  commentDate: PropTypes.string.isRequired,
  commentFixed: PropTypes.bool,
  commentId: PropTypes.number.isRequired,
  commentLikes: PropTypes.number,
  commentStatusName: PropTypes.string.isRequired,
  /**
   * Should be required but there are edge cases where it could be null.
   * There were bugs where users could send an empty comment
   */
  commentText: PropTypes.string,
  hasReply: PropTypes.bool,
  isDisliked: PropTypes.bool,
  isEdited: PropTypes.bool.isRequired,
  isLiked: PropTypes.bool,
  isReply: PropTypes.bool,
  isReview: PropTypes.bool,
  isShowingReplies: PropTypes.bool,
  parentId: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userLevel: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userTypeName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onToggleReplies: PropTypes.func,
};

export default memo(CommentItem);
